import { Dispatch, createAction } from '@reduxjs/toolkit';
import { observationsReportKeyPrefix } from '../../api/queries/keys/reports';
import { queryClient } from '../../api/queryClient';
import { OBSERVATION_CONSTANTS } from '../../constants/observationsConstants';
import type { EquipmentId } from '../equipments';
import type { ParcelId } from '../parcels';
import type { TaskId } from '../tasks';
import type { WorkOrderId } from '../workOrders';
import type { Observation } from './general';

export type ArchivedAssetIds = {
  parcelIds?: ParcelId[];
  equipmentIds?: EquipmentId[];
  taskIds?: TaskId[];
  workOrderIds?: WorkOrderId[];
};

export const observationsArchivedForAssets = createAction<ArchivedAssetIds>(
  'observations/observationsArchivedForAssets',
);

function isObservationOfArchivedAsset(observation: Observation, assetIds: ArchivedAssetIds) {
  const { parcelIds, equipmentIds, taskIds, workOrderIds } = assetIds;
  return Boolean(
    (observation.parcel_id !== undefined && parcelIds?.includes(observation.parcel_id)) ||
    (observation.equipment_instance_id !== undefined && equipmentIds?.includes(observation.equipment_instance_id)) ||
    (observation.task_id !== undefined && taskIds?.includes(observation.task_id)) ||
    (observation.work_order_id !== undefined && workOrderIds?.includes(observation.work_order_id)),
  );
}

// the same array comes back when nothing matched, so buckets holding no observation of an
// archived asset keep their reference and their consumers don't re-render
export function archiveObservationsOfAssets(observations: Observation[], assetIds: ArchivedAssetIds) {
  let changed = false;
  const archived = observations.map((observation) => {
    if (
      observation.status === OBSERVATION_CONSTANTS.STATUS.ARCHIVED ||
      !isObservationOfArchivedAsset(observation, assetIds)
    ) {
      return observation;
    }
    changed = true;
    return { ...observation, status: OBSERVATION_CONSTANTS.STATUS.ARCHIVED };
  });
  return changed ? archived : observations;
}

// the observations report reads React Query, so patching the store alone would leave it stale
export function observationsArchived(assetIds: ArchivedAssetIds, { dispatch }: { dispatch: Dispatch }) {
  dispatch(observationsArchivedForAssets(assetIds));
  void queryClient.invalidateQueries({ queryKey: observationsReportKeyPrefix });
}
